import type { TuningCommand, TuningIdentity, TuningSettingReceipt } from '../contracts/identity';
import type { ITuningClock } from '../contracts/ports';
import type { ResolvedTuningConfiguration } from '../contracts/settings';
import { canonicalTuningConfigurationJson, hashTuningConfiguration } from '../contracts/StrategyRegistry';
import { isTuningPrepare, isTuningResume, isTuningTrialControlContext } from '../contracts/protocol';
import type { TuningPrepared, TuningPrepare, TuningResume, TuningTrialControlContext, TuningWireMessage } from '../contracts/protocol';
import { TrialOperation, TuningTrialError } from './TrialOperation';

export type GuestFirstFrameEvidence = NonNullable<TuningResume['firstFrame']>;
export type TrialFirstFrameEvidence = GuestFirstFrameEvidence & { hostAckReceivedMonotonicMs: number };
export interface ITrialReplacementEndpoint {
  prepare(command: TuningCommand, next: TuningIdentity, resolved: ResolvedTuningConfiguration, deadlineHostMs: number): Promise<void>;
  replace(command: TuningCommand, next: TuningIdentity, resolved: ResolvedTuningConfiguration): Promise<void>;
  firstFrame(next: TuningIdentity): Promise<TrialFirstFrameEvidence>;
  abort(next: TuningIdentity, reason: string): Promise<void>;
}
export type GuestTrialMediaPort = {
  suspendInput(target: TuningIdentity): Promise<void>;
  closeCurrent(target: TuningIdentity): Promise<void>;
  discardPrewarm(): void;
  cancelRecovery(): void;
  reset(target: TuningIdentity, configuration: ResolvedTuningConfiguration): Promise<void>;
  signalingReady(): Promise<void>;
  connect(target: TuningIdentity, configuration: ResolvedTuningConfiguration): Promise<number>;
  firstFrame(target: TuningIdentity, endpointEpoch: number, startedAtMs: number): Promise<GuestFirstFrameEvidence>;
  receipts(target: TuningIdentity): readonly TuningSettingReceipt[];
  resumeInput(target: TuningIdentity): void;
  abort(target: TuningIdentity): Promise<void>;
};
export type GuestTrialLifecycleOptions = {
  clock: ITuningClock; media: GuestTrialMediaPort;
  authorize: (context: TuningTrialControlContext) => boolean;
  emit: (message: TuningWireMessage) => Promise<void>;
  hash?: (configuration: ResolvedTuningConfiguration) => Promise<string>;
};
type PreparedTrial = {
  prepare: TuningPrepare; operation: TrialOperation;
  acknowledgment: Promise<TuningPrepared>; activation?: Promise<TuningResume>;
};

/** Preparation stages configuration; only the matching host commit replaces media. */
export class GuestTrialLifecycle {
  private prepared: PreparedTrial | undefined;

  constructor(private readonly options: GuestTrialLifecycleOptions) {}

  prepare(value: TuningPrepare): Promise<TuningPrepared> {
    if (!isTuningPrepare(value) || !this.options.authorize(value)) {
      return Promise.reject(new TuningTrialError('unauthorized', 'Trial preparation is not authorized'));
    }
    const old = this.prepared;
    if (old && this.sameContext(old.prepare, value)) {
      if (canonicalTuningConfigurationJson(old.prepare.resolvedConfiguration) !== canonicalTuningConfigurationJson(value.resolvedConfiguration)) {
        return Promise.reject(new TuningTrialError('configuration-conflict', 'Command content changed after preparation'));
      }
      return old.acknowledgment;
    }
    if (old?.operation.active()) {
      return Promise.reject(new TuningTrialError('operation-in-progress', 'Another guest trial is prepared'));
    }
    old?.operation.cancel('superseded');
    const trial: PreparedTrial = { prepare: JSON.parse(JSON.stringify(value)) as TuningPrepare,
      operation: new TrialOperation(this.options.clock), acknowledgment: Promise.resolve().then(() => this.validatePrepared(trial)) };
    this.prepared = trial;
    return trial.acknowledgment;
  }

  activate(commit: TuningTrialControlContext): Promise<TuningResume> {
    const trial = this.prepared;
    if (!isTuningTrialControlContext(commit) || !trial || !this.sameContext(trial.prepare, commit) || !this.options.authorize(commit)) {
      return Promise.reject(new TuningTrialError('unprepared-commit', 'Commit does not match a prepared guest trial'));
    }
    if (trial.activation) return trial.activation;
    trial.activation = this.replace(trial);
    return trial.activation;
  }

  async abort(context: TuningTrialControlContext, reason: string): Promise<void> {
    const trial = this.prepared;
    if (!trial || !this.sameContext(trial.prepare, context)) return;
    trial.operation.cancel(reason);
    this.prepared = undefined;
    if (trial.activation) await this.options.media.abort(trial.prepare.target);
  }

  dispose(): void {
    this.prepared?.operation.cancel('disposed'); this.prepared = undefined;
  }

  private async validatePrepared(trial: PreparedTrial): Promise<TuningPrepared> {
    const { prepare, operation } = trial;
    const preparationDeadlineMs = this.options.clock.monotonicMs() + 2_000;
    try {
      const hash = await operation.within(() => (this.options.hash ?? hashTuningConfiguration)(prepare.resolvedConfiguration), 'configuration validation', 2_000);
      if (hash !== prepare.configurationHash || !this.current(trial)) throw new TuningTrialError('configuration-conflict', 'Prepared configuration hash does not match');
      const { resolvedConfiguration: _configuration, ...context } = prepare;
      const acknowledgment: TuningPrepared = { ...context, accepted: true, settings: [] };
      await operation.within(() => this.options.emit({ type: 'stream-tuning', version: 1, kind: 'prepared', payload: acknowledgment }),
        'prepared acknowledgment', preparationDeadlineMs - this.options.clock.monotonicMs());
      return acknowledgment;
    } catch (error) { operation.cancel('preparation-failed'); throw error; }
  }

  private async replace(trial: PreparedTrial): Promise<TuningResume> {
    await trial.acknowledgment;
    const { prepare, operation } = trial, media = this.options.media;
    const stage = async <T>(work: () => Promise<T>, label: string) => {
      const result = await operation.within(work, label);
      if (!this.current(trial)) throw new TuningTrialError('superseded', 'Guest trial was superseded');
      return result;
    };
    try {
      await stage(() => media.suspendInput(prepare.target), 'input neutralization');
      media.cancelRecovery(); media.discardPrewarm();
      await stage(() => media.closeCurrent(prepare.target), 'old guest connection teardown');
      await stage(() => media.reset(prepare.target, prepare.resolvedConfiguration), 'guest trial reset');
      await stage(() => media.signalingReady(), 'signaling readiness');
      const connectionStartMs = this.options.clock.monotonicMs();
      const endpointEpoch = await stage(() => media.connect(prepare.target, prepare.resolvedConfiguration), 'guest connection');
      const { resolvedConfiguration: _configuration, ...context } = prepare;
      await stage(() => this.options.emit({ type: 'stream-tuning', version: 1, kind: 'resume',
        payload: { ...context, stage: 'connected', settings: media.receipts(prepare.target) } }), 'connection acknowledgment');
      const firstFrame = await operation.within(() => media.firstFrame(prepare.target, endpointEpoch, connectionStartMs), 'guest first composition', 10_000);
      if (!this.current(trial) || firstFrame.guestEndpointConnectionEpoch !== endpointEpoch
        || firstFrame.guestConnectionStartMonotonicMs !== connectionStartMs
        || firstFrame.guestCompositionMonotonicMs < connectionStartMs) throw new TuningTrialError('stale-first-frame', 'First composition belongs to an obsolete guest connection');
      const resumed: TuningResume = { ...context, stage: 'first-frame', settings: media.receipts(prepare.target), firstFrame };
      if (!isTuningResume(resumed)) throw new TuningTrialError('invalid-first-frame', 'First composition evidence is unavailable');
      await stage(() => this.options.emit({ type: 'stream-tuning', version: 1, kind: 'resume', payload: resumed }), 'first-frame acknowledgment');
      media.resumeInput(prepare.target); operation.cancel('completed'); return resumed;
    } catch (error) { operation.cancel('replacement-failed'); throw error; }
  }

  private current(trial: PreparedTrial): boolean {
    return this.prepared === trial && trial.operation.active() && this.options.authorize(trial.prepare);
  }

  private sameContext(a: TuningTrialControlContext, b: TuningTrialControlContext): boolean {
    return a.commandId === b.commandId && a.leaseId === b.leaseId && a.configurationHash === b.configurationHash
      && a.strategyId === b.strategyId && a.deadlineHostMs === b.deadlineHostMs
      && a.target.sessionId === b.target.sessionId && a.target.peerId === b.target.peerId
      && a.target.trialId === b.target.trialId && a.target.connectionEpoch === b.target.connectionEpoch
      && a.target.configRevision === b.target.configRevision;
  }
}
